// Fetch layer for /api/overview/*. Every call carries the idToken; the server
// scopes every answer to the projects the signed-in user is assigned to.
import { authHeaders } from '../../utils/authHeader';

async function call(path, { method = 'GET', params, body } = {}) {
  const qs = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ).toString();
  const res = await fetch(`/api/overview${path}${qs ? `?${qs}` : ''}`, {
    method,
    headers: await authHeaders(body ? { 'Content-Type': 'application/json' } : {}),
    body: body ? JSON.stringify(body) : undefined,
  });
  return res;
}

async function json(path, options) {
  const res = await call(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const fetchOverviews = () => json('/list');
export const fetchCandidateProjects = () => json('/projects');
export const createOverview = (payload) => json('', { method: 'POST', body: payload });
export const updateOverview = (id, payload) => json(`/${id}`, { method: 'PUT', body: payload });
export const deleteOverview = (id) => json(`/${id}`, { method: 'DELETE' });
export const fetchCorridors = (id, projectId) => json(`/${id}/corridors`, { params: { project_id: projectId } });
export const fetchSegment = (id, key, projectId) => json(`/${id}/segment`, { params: { key, project_id: projectId } });
export const fetchStations = (id, key, projectId, analysis) => json(`/${id}/stations`, { params: { key, project_id: projectId, analysis } });
export const fetchReportLink = (id, params) => json(`/${id}/report-link`, { params });

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Build the PDF as a server JOB (a large order takes longer than one web request
 * may last): start it, poll its status, then fetch the file. -> {blob, filename}.
 * `onProgress(seconds)` is called on every poll.
 */
export async function fetchPdf(id, payload, { onProgress, pollMs = 2000, timeoutMs = 15 * 60 * 1000 } = {}) {
  const { job } = await json(`/${id}/pdf`, { method: 'POST', body: payload });
  const started = Date.now();
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    await wait(pollMs);
    // eslint-disable-next-line no-await-in-loop
    const state = await json(`/${id}/pdf/${job}`);
    if (onProgress) onProgress(state.seconds || Math.round((Date.now() - started) / 1000));
    if (state.status === 'failed') throw new Error(state.error || 'Could not build the PDF');
    if (state.status === 'ready') {
      // eslint-disable-next-line no-await-in-loop
      const res = await call(`/${id}/pdf/${job}/file`);
      if (!res.ok) throw new Error(`Could not download the PDF (${res.status})`);
      return { blob: await res.blob(), filename: state.filename || 'Corridor_Safety_Summary.pdf' };
    }
    if (Date.now() - started > timeoutMs) throw new Error('The PDF is taking too long. Try fewer roads or fewer rows.');
  }
}

/** Run `worker(item)` over `items`, at most `limit` at a time; results keep their order. */
export async function mapLimit(items, limit, worker) {
  const out = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await worker(items[i], i).then((value) => ({ value }), (error) => ({ error }));
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return out;
}
